import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  Users,
  GitBranch,
  Lock,
  AlertTriangle,
  CheckCircle2,
  Stethoscope,
  X,
  User,
  Heart,
  Droplet,
  Thermometer,
  Wind,
  ClipboardList,
  FileText,
  Pill,
  Radio,
  ChevronRight,
} from "lucide-react";
import { CenterHeader } from "./center/CenterPrimitives";

type Consultation = {
  id: string;
  doctor: string;
  specialty: string;
  type: "consultation" | "eeg" | "ecg" | "imagerie";
  status: "IN_PROGRESS" | "COMMITTED" | "CONFLICT";
  startedAt: string;
  duration: string;
  sagaEvents: number;
  version: number;
  lockedBy?: string;
  room?: string;
  motif?: string;
  notes?: string[];
  prescriptions?: string[];
  exams?: string[];
};

type Patient = {
  id: string;
  name: string;
  dob: string;
  sex: "M" | "F";
  blood: string;
  allergies: string[];
  chronic: string[];
  vitals: { hr: number; bp: string; spo2: number; temp: number; rr: number };
  ipp?: string;
};

type PatientGroup = {
  patient: Patient;
  consultations: Consultation[];
};

const CONCURRENT: PatientGroup[] = [
  {
    patient: {
      id: "P1234",
      ipp: "IPP-024876",
      name: "Mia Miamai",
      dob: "1988-03-14",
      sex: "F",
      blood: "A+",
      allergies: ["Pénicilline", "Latex"],
      chronic: ["HTA", "Asthme léger"],
      vitals: { hr: 92, bp: "138/86", spo2: 96, temp: 37.4, rr: 18 },
    },
    consultations: [
      {
        id: "c1",
        doctor: "Dr Ali BENABDERRAZZAK",
        specialty: "Anesthésie-Réanimation",
        type: "consultation",
        status: "IN_PROGRESS",
        startedAt: "2026-06-19 09:15",
        duration: "30min",
        sagaEvents: 5,
        version: 3,
        room: "Box 4 — Pré-op",
        motif: "Évaluation pré-anesthésique pour cholécystectomie",
        notes: ["ASA II", "Mallampati II", "Pas d'ATCD anesthésique"],
        prescriptions: ["Midazolam 7.5mg PO H-1", "Atropine 0.5mg IV"],
        exams: ["NFS", "TP/TCA", "Ionogramme", "ECG"],
      },
      {
        id: "c2",
        doctor: "Dr Mehdi EL FASSI",
        specialty: "Neurologie",
        type: "consultation",
        status: "IN_PROGRESS",
        startedAt: "2026-06-19 09:20",
        duration: "45min",
        sagaEvents: 3,
        version: 2,
        room: "Cabinet 12",
        motif: "Céphalées récurrentes — bilan",
        notes: ["Céphalées hémicrânie droite", "Pas de signe focal", "Fond d'œil normal"],
        prescriptions: ["Sumatriptan 50mg si crise"],
        exams: ["IRM cérébrale", "EEG de veille"],
      },
      {
        id: "c3",
        doctor: "Dr Sophie MARTIN",
        specialty: "Cardiologie",
        type: "eeg",
        status: "IN_PROGRESS",
        startedAt: "2026-06-19 09:25",
        duration: "60min",
        sagaEvents: 1,
        version: 1,
        lockedBy: "Dr Sophie MARTIN",
        room: "Salle ECG/EEG",
        motif: "EEG dans le cadre du bilan neurologique partagé",
        notes: ["Tracé en cours", "Patient calme"],
        exams: ["EEG 21 électrodes"],
      },
    ],
  },
];

const ALL: Consultation[] = [
  ...CONCURRENT[0].consultations,
  { id: "c4", doctor: "Dr Karim TAZI", specialty: "Radiologie", type: "imagerie", status: "COMMITTED", startedAt: "2026-06-19 08:30", duration: "20min", sagaEvents: 4, version: 1 },
  { id: "c5", doctor: "Dr Laila CHAKIR", specialty: "Pédiatrie", type: "consultation", status: "COMMITTED", startedAt: "2026-06-19 08:45", duration: "25min", sagaEvents: 3, version: 1 },
  { id: "c6", doctor: "Dr Omar BENNANI", specialty: "Dermatologie", type: "consultation", status: "COMMITTED", startedAt: "2026-06-19 09:00", duration: "15min", sagaEvents: 2, version: 1 },
  { id: "c7", doctor: "Dr Yasmine ALAMI", specialty: "Endocrinologie", type: "consultation", status: "COMMITTED", startedAt: "2026-06-19 09:05", duration: "35min", sagaEvents: 6, version: 2 },
];

type TabId = "concurrence" | "toutes" | "pattern";

function Kpi({ label, value, hint, tone = "neutral" }: { label: string; value: string; hint?: string; tone?: "neutral" | "success" | "warning" | "danger" }) {
  const cls = tone === "danger" ? "text-accent" : tone === "warning" ? "text-[oklch(0.85_0.15_85)]" : tone === "success" ? "text-success" : "text-foreground";
  return (
    <div className="card-surface rounded-2xl p-5">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-3 text-4xl font-light tracking-tight ${cls}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function StatusChip({ status }: { status: Consultation["status"] }) {
  const cls =
    status === "IN_PROGRESS"
      ? "bg-success/20 text-success ring-success/30"
      : status === "COMMITTED"
        ? "bg-white/5 text-muted-foreground ring-white/10"
        : "bg-accent/15 text-accent ring-accent/30";
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-medium ring-1 ${cls}`}>
      {status === "IN_PROGRESS" && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" />}
      {status}
    </span>
  );
}

function Chip({ children, tone = "neutral" }: { children: React.ReactNode; tone?: "neutral" | "success" }) {
  const cls = tone === "success" ? "bg-success/15 text-success ring-success/30" : "bg-white/5 text-muted-foreground ring-white/10";
  return <span className={`rounded-md px-2 py-0.5 text-[10px] font-medium ring-1 ${cls}`}>{children}</span>;
}

function ConsultationRow({ c, onSelect, selected }: { c: Consultation; onSelect?: () => void; selected?: boolean }) {
  return (
    <button
      onClick={onSelect}
      disabled={!onSelect}
      className={
        "group flex w-full items-start gap-3 rounded-xl p-3 text-left ring-1 transition-all " +
        (selected
          ? "bg-success/10 ring-success/40"
          : "bg-white/[0.02] ring-white/5 " + (onSelect ? "hover:bg-white/[0.04] hover:ring-white/10 cursor-pointer" : ""))
      }
    >
      <Stethoscope className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{c.doctor}</span>
          <Chip>{c.specialty}</Chip>
          <Chip>{c.type}</Chip>
          <StatusChip status={c.status} />
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <span>Démarrée {c.startedAt}</span>
          <span>·</span>
          <span>{c.duration}</span>
          <span>·</span>
          <span>{c.sagaEvents} sous-événements saga</span>
          {c.lockedBy && (
            <>
              <span>·</span>
              <span className="inline-flex items-center gap-1 text-accent">
                <Lock className="h-3 w-3" /> Lock par {c.lockedBy}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Chip>v{c.version}</Chip>
        {onSelect && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100" />}
      </div>
    </button>
  );
}

export default function ConsultationsAtomiquesView({ onBack }: { onBack: () => void }) {
  const [tab, setTab] = useState<TabId>("concurrence");
  const [openId, setOpenId] = useState<string | null>(null);
  const [liveVitals, setLiveVitals] = useState<Record<string, Patient["vitals"]>>(() =>
    Object.fromEntries(CONCURRENT.map((g) => [g.patient.id, g.patient.vitals])),
  );

  useEffect(() => {
    const jitter = (n: number, d: number, min: number, max: number) =>
      Math.max(min, Math.min(max, +(n + (Math.random() - 0.5) * d).toFixed(1)));
    // Batched updates every 3s; only commit slots whose value actually changed
    // (distinctUntilChanged equivalent) so React skips re-rendering stable tiles.
    const id = setInterval(() => {
      setLiveVitals((prev) => {
        let mutated = false;
        const next: Record<string, Patient["vitals"]> = { ...prev };
        for (const [pid, v] of Object.entries(prev)) {
          const [sysStr, diaStr] = v.bp.split("/");
          const candidate: Patient["vitals"] = {
            hr: Math.round(jitter(v.hr, 4, 55, 130)),
            bp: `${Math.round(jitter(parseInt(sysStr, 10), 4, 95, 165))}/${Math.round(jitter(parseInt(diaStr, 10), 3, 55, 100))}`,
            spo2: Math.round(jitter(v.spo2, 1.5, 90, 100)),
            temp: jitter(v.temp, 0.2, 35.5, 39.5),
            rr: Math.round(jitter(v.rr, 2, 10, 28)),
          };
          const merged: Patient["vitals"] = {
            hr: candidate.hr !== v.hr ? candidate.hr : v.hr,
            bp: candidate.bp !== v.bp ? candidate.bp : v.bp,
            spo2: candidate.spo2 !== v.spo2 ? candidate.spo2 : v.spo2,
            temp: candidate.temp !== v.temp ? candidate.temp : v.temp,
            rr: candidate.rr !== v.rr ? candidate.rr : v.rr,
          };
          const changed =
            merged.hr !== v.hr || merged.bp !== v.bp || merged.spo2 !== v.spo2 || merged.temp !== v.temp || merged.rr !== v.rr;
          if (changed) {
            next[pid] = merged;
            mutated = true;
          }
        }
        return mutated ? next : prev;
      });
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const total = ALL.length;
  const inProgress = ALL.filter((c) => c.status === "IN_PROGRESS").length;
  const concurrent = CONCURRENT.reduce((sum, g) => sum + g.consultations.length, 0);
  const locks = ALL.filter((c) => c.lockedBy).length;

  const selectedPair = useMemo(() => {
    if (!openId) return null;
    for (const g of CONCURRENT) {
      const c = g.consultations.find((x) => x.id === openId);
      if (c) {
        const patient = { ...g.patient, vitals: liveVitals[g.patient.id] ?? g.patient.vitals };
        return { patient, consultation: c, group: g };
      }
    }
    return null;
  }, [openId, liveVitals]);

  return (
    <div className="flex flex-col gap-5">
      <CenterHeader title="Consultations atomiques" scope="Optimistic locking · Un patient peut avoir plusieurs consultations simultanées avec différents praticiens" onBack={onBack} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
        <Kpi label="Total aujourd'hui" value={String(total)} />
        <Kpi label="En cours" value={String(inProgress)} tone="success" />
        <Kpi label="Concurrence (multi-praticien)" value={String(concurrent)} hint="même patient" tone="warning" />
        <Kpi label="Lock explicite" value={String(locks)} hint="accès restreint" tone="danger" />
      </div>

      <div className="card-surface inline-flex w-fit items-center gap-1 rounded-full p-1">
        {([
          { id: "concurrence", label: `Concurrence (${CONCURRENT.length})`, icon: Users },
          { id: "toutes", label: `Toutes (${total})`, icon: Activity },
          { id: "pattern", label: "Pattern atomicité", icon: GitBranch },
        ] as { id: TabId; label: string; icon: typeof Users }[]).map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={
                "inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all " +
                (active ? "bg-success/20 text-success ring-1 ring-success/40" : "text-muted-foreground hover:text-foreground hover:bg-white/5")
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "concurrence" && (
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-2xl bg-[oklch(0.82_0.16_85)]/10 p-4 ring-1 ring-[oklch(0.82_0.16_85)]/30">
            <AlertTriangle className="h-4 w-4 shrink-0 text-[oklch(0.85_0.15_85)]" />
            <div className="text-xs">
              <div className="font-medium text-[oklch(0.85_0.15_85)]">Consultations concurrentes sur un même patient</div>
              <p className="mt-1 leading-relaxed text-muted-foreground">
                L'atomicité est garantie par <span className="font-medium text-foreground">version vector</span> et <span className="font-medium text-foreground">optimistic locking</span> sur l'entité consultation. Cliquez sur un praticien pour ouvrir le détail temps réel.
              </p>
            </div>
          </div>

          {CONCURRENT.map((g) => (
            <div key={g.patient.id} className="card-surface rounded-2xl p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-base font-medium">{g.patient.name}</span>
                  <Chip>{g.patient.id}</Chip>
                </div>
                <Chip tone="success">{g.consultations.length} consultations simultanées</Chip>
              </div>

              <div className="mt-4 space-y-2">
                {g.consultations.map((c) => (
                  <ConsultationRow key={c.id} c={c} onSelect={() => setOpenId(c.id)} selected={openId === c.id} />
                ))}
              </div>

              <div className="mt-4 flex items-center gap-2 rounded-xl bg-success/10 p-3 ring-1 ring-success/30">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span className="text-xs text-success">Atomicité garantie : chaque consultation est un sous-graphe indépendant du patient</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "toutes" && (
        <div className="card-surface rounded-2xl p-5">
          <div className="space-y-2">
            {ALL.map((c) => {
              const isLive = CONCURRENT.some((g) => g.consultations.some((x) => x.id === c.id));
              return (
                <ConsultationRow
                  key={c.id}
                  c={c}
                  onSelect={isLive ? () => setOpenId(c.id) : undefined}
                  selected={openId === c.id}
                />
              );
            })}
          </div>
        </div>
      )}

      {tab === "pattern" && (
        <div className="card-surface space-y-4 rounded-2xl p-6 text-sm">
          <div className="text-base font-medium">Pattern atomicité — Saga + Version Vector</div>
          <ol className="space-y-3 text-xs text-muted-foreground">
            <li><span className="font-medium text-foreground">1. Brouillon indépendant</span> — Chaque consultation ouverte génère un draft local versionné.</li>
            <li><span className="font-medium text-foreground">2. Saga de sous-événements</span> — Notes, prescriptions, examens émis comme événements idempotents.</li>
            <li><span className="font-medium text-foreground">3. Optimistic locking au commit</span> — Comparaison de version vector ; conflit ⇒ merge guidé.</li>
            <li><span className="font-medium text-foreground">4. Lock explicite</span> — Pour les actes invasifs ; libéré au commit ou timeout.</li>
            <li><span className="font-medium text-foreground">5. Audit immuable</span> — Journalisation WORM ; rollback via event replay.</li>
          </ol>
        </div>
      )}

      {selectedPair && (
        <DrawerHost
          pair={selectedPair}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

function DrawerHost({
  pair,
  onClose,
}: {
  pair: { patient: Patient; consultation: Consultation; group: PatientGroup };
  onClose: () => void;
}) {
  const siblings = useMemo(
    () => pair.group.consultations.filter((x) => x.id !== pair.consultation.id),
    [pair.group, pair.consultation.id],
  );
  return (
    <DoctorPatientDrawer
      patient={pair.patient}
      consultation={pair.consultation}
      siblings={siblings}
      onClose={onClose}
    />
  );
}

const VitalTile = memo(function VitalTile({
  icon: Icon,
  label,
  value,
  unit,
}: { icon: typeof Heart; label: string; value: string | number; unit?: string }) {
  const prevRef = useRef(value);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (prevRef.current !== value) {
      prevRef.current = value;
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 800);
      return () => clearTimeout(t);
    }
  }, [value]);
  return (
    <div
      className={
        "rounded-xl p-3 ring-1 transition-colors duration-500 " +
        (flash ? "bg-success/15 ring-success/40" : "bg-white/[0.03] ring-white/5")
      }
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="mt-1 text-xl font-light">
        <span key={String(value)} className={flash ? "inline-block animate-fade-in" : "inline-block"}>
          {value}
        </span>
        {unit && <span className="ml-1 text-[10px] text-muted-foreground">{unit}</span>}
      </div>
    </div>
  );
});

const DoctorPatientDrawer = memo(function DoctorPatientDrawer({
  patient,
  consultation,
  siblings,
  onClose,
}: {
  patient: Patient;
  consultation: Consultation;
  siblings: Consultation[];
  onClose: () => void;
}) {
  const age = new Date().getFullYear() - new Date(patient.dob).getFullYear();
  return (
    <aside className="fixed right-0 top-0 z-40 flex h-screen w-full max-w-xl flex-col overflow-y-auto border-l border-white/10 bg-card p-6 shadow-2xl animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Stethoscope className="h-4 w-4 text-success" />
              <span className="text-base font-semibold">{consultation.doctor}</span>
              <Chip>{consultation.specialty}</Chip>
              <StatusChip status={consultation.status} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
              <Radio className="h-3 w-3 text-success" />
              <span className="text-success">Temps réel</span>
              <span>·</span>
              <span>{consultation.room ?? "—"}</span>
              <span>·</span>
              <span>v{consultation.version} · {consultation.sagaEvents} événements saga</span>
            </div>
          </div>
          <button onClick={onClose} className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-white/5 hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Patient identity */}
        <div className="mt-5 rounded-2xl bg-white/[0.03] p-4 ring-1 ring-white/5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-success/20 text-success">
                <User className="h-5 w-5" />
              </div>
              <div>
                <div className="text-lg font-medium">{patient.name}</div>
                <div className="text-xs text-muted-foreground">
                  {patient.sex === "F" ? "Femme" : "Homme"} · {age} ans · né(e) {patient.dob}
                </div>
              </div>
            </div>
            <div className="text-right text-[11px] text-muted-foreground">
              <div>{patient.id}</div>
              {patient.ipp && <div className="font-mono">{patient.ipp}</div>}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">Groupe sanguin</div>
              <div className="mt-0.5 font-medium">{patient.blood}</div>
            </div>
            <div className="col-span-2">
              <div className="text-[10px] uppercase text-muted-foreground">Allergies</div>
              <div className="mt-0.5 flex flex-wrap gap-1.5">
                {patient.allergies.map((a) => (
                  <span key={a} className="rounded-md bg-accent/15 px-2 py-0.5 text-[10px] text-accent ring-1 ring-accent/30">{a}</span>
                ))}
              </div>
            </div>
            <div className="col-span-3">
              <div className="text-[10px] uppercase text-muted-foreground">Antécédents</div>
              <div className="mt-0.5 flex flex-wrap gap-1.5">
                {patient.chronic.map((a) => <Chip key={a}>{a}</Chip>)}
              </div>
            </div>
          </div>
        </div>

        {/* Vitals live */}
        <div className="mt-4">
          <div className="mb-2 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            <Radio className="h-3 w-3 text-success animate-pulse" />
            Constantes en direct
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <VitalTile icon={Heart} label="FC" value={patient.vitals.hr} unit="bpm" />
            <VitalTile icon={Activity} label="TA" value={patient.vitals.bp} unit="mmHg" />
            <VitalTile icon={Droplet} label="SpO₂" value={patient.vitals.spo2} unit="%" />
            <VitalTile icon={Thermometer} label="T°" value={patient.vitals.temp} unit="°C" />
            <VitalTile icon={Wind} label="FR" value={patient.vitals.rr} unit="/min" />
          </div>
        </div>

        {/* Consultation detail */}
        <div className="mt-4 rounded-2xl bg-white/[0.03] p-4 ring-1 ring-white/5">
          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Motif</div>
          <div className="mt-1 text-sm">{consultation.motif ?? "—"}</div>
          <div className="mt-1 text-[11px] text-muted-foreground">Démarrée {consultation.startedAt} · {consultation.duration}</div>

          {consultation.notes && consultation.notes.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                <FileText className="h-3 w-3" /> Notes cliniques
              </div>
              <ul className="mt-2 space-y-1 text-xs">
                {consultation.notes.map((n, i) => (
                  <li key={i} className="rounded-md bg-white/[0.02] px-2.5 py-1.5">{n}</li>
                ))}
              </ul>
            </div>
          )}

          {consultation.prescriptions && consultation.prescriptions.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                <Pill className="h-3 w-3" /> Prescriptions
              </div>
              <ul className="mt-2 space-y-1 text-xs">
                {consultation.prescriptions.map((p, i) => (
                  <li key={i} className="rounded-md bg-white/[0.02] px-2.5 py-1.5">{p}</li>
                ))}
              </ul>
            </div>
          )}

          {consultation.exams && consultation.exams.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                <ClipboardList className="h-3 w-3" /> Examens demandés
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {consultation.exams.map((e) => <Chip key={e} tone="success">{e}</Chip>)}
              </div>
            </div>
          )}
        </div>

        {/* Siblings */}
        {siblings.length > 0 && (
          <div className="mt-4">
            <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Autres consultations actives sur ce patient
            </div>
            <div className="space-y-2">
              {siblings.map((s) => <ConsultationRow key={s.id} c={s} />)}
            </div>
          </div>
        )}

        {consultation.lockedBy && (
          <div className="mt-4 flex items-center gap-2 rounded-xl bg-accent/10 p-3 text-xs text-accent ring-1 ring-accent/30">
            <Lock className="h-4 w-4" />
            Ressource verrouillée par <span className="font-medium">{consultation.lockedBy}</span> — les autres praticiens peuvent lire mais pas modifier.
          </div>
        )}
    </aside>
  );
});
